/**
 * Guest announcements API — Phase 5c sub-commit 2 (13 Jun 2026).
 *
 * TanStack Query wrappers around the three guest-facing announcement
 * endpoints exposed by towninsider-api routes/announcements.ts:
 *
 *   GET  /v1/announcements                — useInbox          (list)
 *   GET  /v1/announcements/unread-count   — useUnreadCount    (badge)
 *   POST /v1/announcements/:id/read       — useMarkAnnouncementRead
 *                                            (idempotent mark-read)
 *
 * Architecture mirrors the existing read+mutation pairs in this repo
 * (`useUser.ts`, `chatHistory.ts`):
 *   - apiFetch boundary with inline Zod schemas
 *   - `useAuth()` for token + userId
 *   - Identity-scoped query keys via `userId ?? null` — see below
 *
 * --------------------------------------------------------------------
 * Identity-scoped query keys
 * --------------------------------------------------------------------
 *
 *   inboxQueryKey(userId)        → ['announcements', 'inbox', userId]
 *   unreadCountQueryKey(userId)  → ['announcements', 'unread-count', userId]
 *
 * Account A's inbox is cached under one key, account B's under
 * another — two different cache entries, no shared key from which a
 * cross-account read could occur. Same structural fix as `useUser`
 * (10 Jun) and `chatHistory` (12 Jun): makes the bleed impossible
 * rather than just hygienically prevented.
 *
 * The factories are EXPORTED so external callers — specifically the
 * SW push-message listener wired in sub-commit 3 — can construct the
 * keys without duplicating the literal. Single source of truth on
 * the key shape.
 *
 * --------------------------------------------------------------------
 * Optimistic mark-read with rollback
 * --------------------------------------------------------------------
 *
 * Mark-read is the only place in this hook trio that benefits from
 * optimistic updates (the other two are read-only queries).
 *
 *   onMutate:
 *     1. Cancel in-flight queries for inbox + unread-count so they
 *        can't overwrite our optimistic snapshot on resolution.
 *     2. Snapshot both caches (returned as the mutation `context`).
 *     3. Write inbox cache: target row's `isRead` → true.
 *     4. Decrement unread-count cache by 1, but ONLY if the row was
 *        actually unread before — re-marking an already-read row
 *        must NOT bring the badge below 0.
 *
 *   onError:
 *     - Restore both caches from the snapshot.
 *
 *   onSettled:
 *     - Invalidate both queries. Triggers a refetch (if anyone is
 *       subscribed) that reconciles any divergence between the
 *       optimistic write and the canonical server state — e.g. a
 *       concurrent read from another device, or the announcement
 *       being archived between optimistic write and server response.
 *
 * Why optimistic when existing hooks (`useUpdateProfile`) use plain
 * onSuccess-setQueryData: mark-read fires automatically from the
 * InboxPage's IntersectionObserver on dwell (no explicit user
 * action to wait on), and the visible-impact case is the bell-icon
 * badge on MePage. A user who taps a notification → lands on
 * /inbox → row marks read → navigates back to /me must see the
 * badge decrement instantly. Without optimistic, a 30s staleTime
 * means `refetchOnMount` won't fire on a write made 1s ago, so the
 * badge stays stale. Optimistic makes the bell real-time.
 *
 * --------------------------------------------------------------------
 * Schema notes (guest projection)
 * --------------------------------------------------------------------
 *
 * The server strips `createdByClerkUserId` from each row before
 * responding — guests don't see partner-admin identities.
 *
 * `deliveryStats` and `archivedAt` are marked `.optional()` defensively:
 *   - `deliveryStats` is partner-facing analytics. Per the Phase 5
 *     principle "Aggregate reads, not per-guest visibility — partners
 *     see '23 of 47 opened', never 'Mick read it at 14:32'", the
 *     guest projection ideally strips them. If the server tightens
 *     this projection in future, no client breakage. The UI never
 *     reads it on the guest side regardless.
 *   - `archivedAt` is always null in practice — the server filters
 *     `listAnnouncementsForGuest` on `archived_at IS NULL` — but the
 *     schema is loose enough to tolerate either projection choice.
 */

import { useAuth } from '@clerk/clerk-react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch } from '../api';

// ─── Wire schemas ────────────────────────────────────────────────────

const deliveryStatsSchema = z.object({
  attempted: z.number().int().min(0),
  delivered: z.number().int().min(0),
  gone: z.number().int().min(0),
  failed: z.number().int().min(0),
});

const inboxAnnouncementSchema = z.object({
  id: z.string().min(1),
  partnerId: z.string().min(1),
  title: z.string(),
  body: z.string(),
  urlTarget: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  isRead: z.boolean(),
  // Defensive — see file docstring.
  deliveryStats: deliveryStatsSchema.nullable().optional(),
  archivedAt: z.string().nullable().optional(),
});

export type InboxAnnouncement = z.infer<typeof inboxAnnouncementSchema>;

const inboxResponseSchema = z.object({
  announcements: z.array(inboxAnnouncementSchema),
});

export type InboxResponse = z.infer<typeof inboxResponseSchema>;

const unreadCountResponseSchema = z.object({
  count: z.number().int().min(0),
});

export type UnreadCountResponse = z.infer<typeof unreadCountResponseSchema>;

const markReadResponseSchema = z.object({
  ok: z.literal(true),
});

export type MarkReadResponse = z.infer<typeof markReadResponseSchema>;

// ─── Query key factories (EXPORTED for cross-module invalidation) ───

/**
 * Identity-scoped key for the inbox list query. The `string | null`
 * parameter accepts Clerk's `useAuth().userId` shape directly without
 * forcing callers to coerce; `null` keeps the key shape stable
 * across Clerk's loading window (the `enabled` gate blocks fetches
 * against the null-key variant so no stray request fires).
 */
export function inboxQueryKey(
  userId: string | null,
): readonly ['announcements', 'inbox', string | null] {
  return ['announcements', 'inbox', userId] as const;
}

/**
 * Identity-scoped key for the unread-count query. Same shape and
 * rationale as `inboxQueryKey`. Used by the bell-icon badge on
 * MePage (sub-commit 5) and invalidated by the SW push-message
 * listener (sub-commit 3) for real-time badge updates.
 */
export function unreadCountQueryKey(
  userId: string | null,
): readonly ['announcements', 'unread-count', string | null] {
  return ['announcements', 'unread-count', userId] as const;
}

// ─── Fetchers ────────────────────────────────────────────────────────

interface FetcherArgs {
  getToken: () => Promise<string | null>;
}

export async function getInbox({
  getToken,
}: FetcherArgs): Promise<InboxResponse> {
  return apiFetch('/v1/announcements', {
    schema: inboxResponseSchema,
    getToken,
  });
}

export async function getUnreadCount({
  getToken,
}: FetcherArgs): Promise<UnreadCountResponse> {
  return apiFetch('/v1/announcements/unread-count', {
    schema: unreadCountResponseSchema,
    getToken,
  });
}

interface MarkReadArgs extends FetcherArgs {
  announcementId: string;
}

export async function markAnnouncementRead({
  announcementId,
  getToken,
}: MarkReadArgs): Promise<MarkReadResponse> {
  return apiFetch(
    `/v1/announcements/${encodeURIComponent(announcementId)}/read`,
    {
      method: 'POST',
      schema: markReadResponseSchema,
      getToken,
    },
  );
}

// ─── Hooks ───────────────────────────────────────────────────────────

export interface UseAnnouncementsQueryOptions {
  /**
   * Gate the fetch on top of the built-in signed-in check. Default
   * true. Pass false in tests, or in contexts that want to defer the
   * fetch (e.g. an inbox component mounted but hidden behind a tab).
   */
  enabled?: boolean;
}

export function useInbox(
  options?: UseAnnouncementsQueryOptions,
): UseQueryResult<InboxResponse> {
  const { isSignedIn, userId, getToken } = useAuth();
  return useQuery<InboxResponse>({
    queryKey: inboxQueryKey(userId ?? null),
    queryFn: () => getInbox({ getToken }),
    enabled: !!isSignedIn && !!userId && (options?.enabled ?? true),
    // 30s mirrors useChatHistory — announcements are infrequent but
    // when they arrive (via push), real-time visibility matters. The
    // explicit SW-postMessage invalidation in sub-commit 3 makes the
    // staleTime largely moot for the open-app case; this value
    // governs the closed-app refetch-on-focus cadence.
    staleTime: 30 * 1000,
  });
}

export function useUnreadCount(
  options?: UseAnnouncementsQueryOptions,
): UseQueryResult<UnreadCountResponse> {
  const { isSignedIn, userId, getToken } = useAuth();
  return useQuery<UnreadCountResponse>({
    queryKey: unreadCountQueryKey(userId ?? null),
    queryFn: () => getUnreadCount({ getToken }),
    enabled: !!isSignedIn && !!userId && (options?.enabled ?? true),
    staleTime: 30 * 1000,
  });
}

/**
 * Mutation context for the optimistic snapshot/rollback dance.
 * Module-private — callers should treat the hook as
 * `useMutation<MarkReadResponse, Error, string>` and ignore the
 * fourth type param at the call site.
 */
interface MarkReadMutationContext {
  prevInbox: InboxResponse | undefined;
  prevUnread: UnreadCountResponse | undefined;
}

export function useMarkAnnouncementRead(): UseMutationResult<
  MarkReadResponse,
  Error,
  string,
  MarkReadMutationContext
> {
  const { userId, getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation<
    MarkReadResponse,
    Error,
    string,
    MarkReadMutationContext
  >({
    mutationFn: (announcementId) =>
      markAnnouncementRead({ announcementId, getToken }),

    onMutate: async (announcementId) => {
      const inboxKey = inboxQueryKey(userId ?? null);
      const unreadKey = unreadCountQueryKey(userId ?? null);

      // Cancel any in-flight queries — without this, a queryFn that
      // resolves AFTER our optimistic setQueryData would overwrite
      // the optimistic value with stale server data.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: inboxKey }),
        queryClient.cancelQueries({ queryKey: unreadKey }),
      ]);

      const prevInbox = queryClient.getQueryData<InboxResponse>(inboxKey);
      const prevUnread =
        queryClient.getQueryData<UnreadCountResponse>(unreadKey);

      // Idempotent delta — re-marking an already-read row must not
      // double-decrement the badge.
      const wasUnread =
        prevInbox?.announcements.some(
          (a) => a.id === announcementId && !a.isRead,
        ) ?? false;

      if (prevInbox !== undefined) {
        queryClient.setQueryData<InboxResponse>(inboxKey, {
          ...prevInbox,
          announcements: prevInbox.announcements.map((a) =>
            a.id === announcementId ? { ...a, isRead: true } : a,
          ),
        });
      }

      if (wasUnread && prevUnread !== undefined) {
        queryClient.setQueryData<UnreadCountResponse>(unreadKey, {
          count: Math.max(0, prevUnread.count - 1),
        });
      }

      return { prevInbox, prevUnread };
    },

    onError: (_err, _id, context) => {
      if (context === undefined) return;
      const inboxKey = inboxQueryKey(userId ?? null);
      const unreadKey = unreadCountQueryKey(userId ?? null);
      if (context.prevInbox !== undefined) {
        queryClient.setQueryData(inboxKey, context.prevInbox);
      }
      if (context.prevUnread !== undefined) {
        queryClient.setQueryData(unreadKey, context.prevUnread);
      }
    },

    onSettled: () => {
      // Reconcile with server. Covers cases where the optimistic
      // write diverged from server state — concurrent read from
      // another device, announcement archived between optimistic
      // write and server response, etc.
      void queryClient.invalidateQueries({
        queryKey: inboxQueryKey(userId ?? null),
      });
      void queryClient.invalidateQueries({
        queryKey: unreadCountQueryKey(userId ?? null),
      });
    },
  });
}
